import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { activitiesApi, uploadApi, messagingApi, registrationsApi } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ChevronLeft, Upload, Camera, Check, Image as ImageIcon, Info, X } from 'lucide-react';

const PaymentProof = () => {
  const { activityId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const creatorId = searchParams.get('creator');

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const res = await activitiesApi.getById(activityId);
        setActivity(res.data);
      } catch {
        toast.error('Error al cargar la actividad');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [activityId, navigate]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUploadAndSend = async () => {
    if (!selectedFile) return;
    setUploading(true);

    try {
      // 1. Upload image
      const formData = new FormData();
      formData.append('file', selectedFile);
      const uploadRes = await uploadApi.upload(formData);
      const imageUrl = uploadRes.data.url;

      // 2. Send message with image to creator
      const recipientId = creatorId || activity?.creator_id;
      await messagingApi.sendMessage({
        recipient_id: recipientId,
        activity_id: activityId,
        content: '📎 Comprobante de pago adjunto',
        image_url: imageUrl,
      });

      setUploaded(true);
      toast.success('Comprobante enviado correctamente');
    } catch (error) {
      // error handled silently
      toast.error('Error al enviar el comprobante. Intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const price = activity?.price || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 safe-top">
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate('/my-activities', { replace: true })}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="font-rubik font-semibold">Confirmar pago</h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Activity summary */}
        <div className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-4">
          {activity?.cover_image && (
            <img
              src={activity.cover_image}
              alt={activity.title}
              className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <h2 className="font-semibold text-gray-800 text-sm truncate">{activity?.title}</h2>
            <p className="text-xs text-gray-400">{activity?.creator_name}</p>
            {price > 0 && (
              <p className="text-primary font-bold text-sm mt-1">{formatPrice(price, 'es-MX')}</p>
            )}
          </div>
        </div>

        {/* Deadline warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 leading-relaxed">
            Tienes <strong>24 horas</strong> desde tu reserva para enviar tu comprobante. Si no se confirma el pago en 48 horas, tu reserva será cancelada automáticamente.
          </p>
        </div>

        {/* Payment info */}
        {(activity?.bank_info || activity?.payment_link) && (
          <div className="bg-white rounded-2xl p-5 border border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm text-gray-700">Datos de pago</h3>
            </div>
            {activity.bank_info && (
              <div className="bg-gray-50 rounded-xl p-3 mb-3">
                <p className="text-sm text-gray-600 whitespace-pre-line">{activity.bank_info}</p>
              </div>
            )}
            {activity.payment_ref_code && (
              <p className="text-xs text-gray-400">
                Referencia: <span className="font-mono font-medium text-gray-600">{activity.payment_ref_code}</span>
              </p>
            )}
            {activity.payment_link && (
              <a
                href={activity.payment_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 text-sm text-primary font-medium hover:underline"
              >
                Ir al link de pago
              </a>
            )}
          </div>
        )}

        {/* Upload section */}
        {!uploaded ? (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-5">
              <h3 className="font-semibold text-gray-800 mb-1">Sube tu comprobante de pago</h3>
              <p className="text-sm text-gray-400 mb-4">
                Adjunta una captura o foto de tu comprobante. El creador lo revisará y confirmará tu reserva.
              </p>

              {/* File preview or upload area */}
              {previewUrl ? (
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="Comprobante"
                    className="w-full max-h-72 object-contain rounded-xl border border-gray-200 bg-gray-50"
                    data-testid="proof-preview"
                  />
                  <button
                    onClick={handleRemoveFile}
                    className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-3 hover:border-primary/40 hover:bg-primary/5 transition-all"
                  data-testid="upload-proof-area"
                >
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                    <Camera className="w-7 h-7 text-gray-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">Toca para subir imagen</p>
                    <p className="text-xs text-gray-300 mt-1">JPG, PNG o WEBP · Máx 5MB</p>
                  </div>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="proof-file-input"
              />
            </div>

            {/* Submit button */}
            <div className="px-5 pb-5">
              <Button
                onClick={handleUploadAndSend}
                disabled={!selectedFile || uploading}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl h-12 font-semibold disabled:opacity-40"
                data-testid="send-proof-btn"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Enviar comprobante al creador
                  </span>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Success state */
          <div className="bg-white rounded-2xl p-8 border border-gray-100 text-center animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-rubik text-xl font-bold text-gray-800 mb-2">¡Comprobante enviado!</h3>
            <p className="text-sm text-gray-400 mb-6">
              El organizador revisará tu comprobante y confirmará tu reserva. Recibirás una notificación cuando esté confirmada.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate(`/messages?to=${creatorId || activity?.creator_id}&activity=${activityId}`)}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl h-12 font-semibold"
                data-testid="proof-view-chat-btn"
              >
                Ver chat actividad
              </Button>
              <Button
                onClick={() => navigate('/my-activities')}
                variant="outline"
                className="w-full rounded-xl h-12"
              >
                Ver mis reservas
              </Button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PaymentProof;
